import { ChevronLeftIcon, FolderIcon } from '@heroicons/react/24/outline'
import { redirect, type LoaderFunctionArgs, type MetaFunction } from 'react-router'
import { href, Link, useLoaderData } from 'react-router'
import ProductCard from '~/components/product-card'
import { getTenantContext } from '~/services/authz.server'
import { getOrganizationDetailsByOrgId } from '~/services/org-details.server'
import { getProductsCached } from '~/services/products.cache.server'
import { useShopContext } from '~/utils/use-shop-context'

export async function loader(args: LoaderFunctionArgs) {
  const categoryKey = args.params.key
  const tc = await getTenantContext(args)

  // Build canonical URL from request
  const url = new URL(args.request.url)
  const canonicalUrl = `${url.origin}/category/${categoryKey}`

  // B2B products should be visible to admins and webshop owners
  const canSeeB2B = tc.isAdmin || tc.orgRole === 'webshop_owner'

  const [products, orgDetails] = await Promise.all([
    getProductsCached({ organizationId: tc.org?.id, includingB2B: canSeeB2B }),
    getOrganizationDetailsByOrgId(tc.org?.id)
  ])

  // Find the category from the first matching product
  const categoryProduct = products.find(p => p.category.key === categoryKey)

  if (!categoryKey || !categoryProduct) {
    return redirect(href('/'))
  }

  const category = categoryProduct.category

  // Get products in this category for structured data
  const categoryProducts = products.filter(p => p.category.key === categoryKey)

  return {
    categoryKey,
    categoryLabel: category.label,
    productCount: categoryProducts.length,
    companyName: orgDetails.companyName,
    canonicalUrl
  }
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  if (!data) return []

  const title = `${data.categoryLabel} – ${data.companyName} webshop`
  const description = `Bekijk alle ${data.productCount} producten in de categorie ${data.categoryLabel}`

  // JSON-LD CollectionPage schema for LLM optimization
  const collectionJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: data.categoryLabel,
    description: description,
    url: data.canonicalUrl,
    isPartOf: {
      '@type': 'WebSite',
      name: `${data.companyName} webshop`
    },
    about: {
      '@type': 'Thing',
      name: data.categoryLabel
    },
    numberOfItems: data.productCount
  }

  return [
    { title },
    { name: 'description', content: description },
    { tagName: 'link', rel: 'canonical', href: data.canonicalUrl },
    // OpenGraph tags
    { property: 'og:title', content: title },
    { property: 'og:description', content: description },
    { property: 'og:type', content: 'website' },
    { property: 'og:url', content: data.canonicalUrl },
    { property: 'og:site_name', content: `${data.companyName} webshop` },
    { property: 'og:locale', content: 'nl_NL' },
    // JSON-LD structured data
    { 'script:ld+json': collectionJsonLd }
  ]
}

export default function CategoryPage() {
  const { categoryKey, categoryLabel, productCount } = useLoaderData<typeof loader>()
  const { products } = useShopContext()

  const filteredProducts = products.filter(p => p.category.key === categoryKey)

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-1 flex-col p-4 lg:p-2 lg:pt-4">
      {/* Breadcrumb Navigation */}
      <nav className="mb-6">
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-sm font-medium text-(--ws-link) transition-colors duration-200 hover:text-(--ws-link-hover)">
          <ChevronLeftIcon className="h-4 w-4" />
          Alle producten
        </Link>
      </nav>

      {/* Category Header */}
      <header className="mb-8">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-(--ws-radius-inner) bg-(--ws-link-tint) text-(--ws-link)">
            <FolderIcon className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-title text-(--ws-heading)">{categoryLabel}</h1>
            <p className="mt-1 text-sm text-(--ws-text-muted) tabular-nums">
              {productCount} {productCount === 1 ? 'product' : 'producten'}
            </p>
          </div>
        </div>
      </header>

      {/* Products Grid */}
      {filteredProducts.length > 0 ? (
        <div className="grid w-full grid-cols-1 items-stretch gap-7 sm:grid-cols-2 md:gap-8 lg:grid-cols-3">
          {filteredProducts.map(product => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>
      ) : (
        <div className="ws-tile-empty">
          <div className="mb-4 flex size-16 items-center justify-center rounded-full bg-(--ws-accent-tint) text-(--ws-accent)">
            <FolderIcon className="size-8" strokeWidth={1.5} />
          </div>
          <p className="text-lg font-medium text-(--ws-text)">Geen producten in deze categorie</p>
          <p className="mt-1 text-sm text-(--ws-text-muted)">Kom later terug voor nieuwe producten.</p>
        </div>
      )}
    </div>
  )
}
